<script setup>
import { computed, ref } from 'vue';
import { getInboxIconByType, INBOX_TYPES } from 'dashboard/helper/inbox';
import { useRouter, useRoute } from 'vue-router';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper.js';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import CardMessagePreview from './CardMessagePreview.vue';
import CardMessagePreviewWithMeta from './CardMessagePreviewWithMeta.vue';
import CardPriorityIcon from './CardPriorityIcon.vue';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
  contact: {
    type: Object,
    required: true,
  },
  stateInbox: {
    type: Object,
    required: true,
  },
  accountLabels: {
    type: Array,
    required: true,
  },
});

const router = useRouter();
const route = useRoute();

const cardMessagePreviewWithMetaRef = ref(null);

const currentContact = computed(() => props.contact);

const currentContactName = computed(() => currentContact.value?.name);
const currentContactThumbnail = computed(() => currentContact.value?.thumbnail);
const currentContactStatus = computed(
  () => currentContact.value?.availabilityStatus
);

const inbox = computed(() => props.stateInbox);

const inboxName = computed(() => inbox.value?.name);

const inboxIcon = computed(() => {
  const { channelType, medium } = inbox.value;
  return getInboxIconByType(channelType, medium);
});

const CHANNEL_COLORS = {
  [INBOX_TYPES.FB]:        '#0084FF', // Messenger mavi
  [INBOX_TYPES.INSTAGRAM]: '#C13584', // Instagram mor
  [INBOX_TYPES.TELEGRAM]:  '#2AABEE', // Telegram açık mavi
  [INBOX_TYPES.WEB]:       '#1a56db', // Website koyu mavi
  [INBOX_TYPES.WHATSAPP]:  '#25D366', // WhatsApp yeşil
  [INBOX_TYPES.EMAIL]:     '#EA4335', // Email kırmızı
  [INBOX_TYPES.TIKTOK]:    '#010101', // TikTok siyah
  [INBOX_TYPES.TWITTER]:   '#000000', // X/Twitter siyah
  [INBOX_TYPES.SMS]:       '#6B7280', // SMS gri
  [INBOX_TYPES.API]:       '#6B7280', // API gri
};

const channelColor = computed(
  () => CHANNEL_COLORS[inbox.value?.channelType] || '#6B7280'
);

const inboxBadgeStyle = computed(() => ({
  backgroundColor: channelColor.value,
}));

const cardBorderStyle = computed(() => ({
  borderLeft: `3px solid ${channelColor.value}`,
}));

const lastActivityAt = computed(() => {
  const timestamp = props.conversation?.timestamp;
  return timestamp ? shortTimestamp(dynamicTime(timestamp)) : '';
});

const showMessagePreviewWithoutMeta = computed(() => {
  const { labels = [] } = props.conversation;
  return (
    !cardMessagePreviewWithMetaRef.value?.hasSlaThreshold && labels.length === 0
  );
});

const onCardClick = e => {
  const path = frontendURL(
    conversationUrl({
      accountId: route.params.accountId,
      id: props.conversation.id,
    })
  );

  if (e.metaKey || e.ctrlKey) {
    window.open(
      window.chatwootConfig.hostURL + path,
      '_blank',
      'noopener noreferrer nofollow'
    );
    return;
  }
  router.push({ path });
};
</script>

<template>
  <div
    role="button"
    class="flex w-full gap-3 py-4 ltr:pl-3 rtl:pr-3 ltr:pr-3 rtl:pl-3 transition-all duration-300 ease-in-out cursor-pointer"
    :style="cardBorderStyle"
    @click="onCardClick"
  >
    <Avatar
      :name="currentContactName"
      :src="currentContactThumbnail"
      :size="24"
      :status="currentContactStatus"
      rounded-full
    />
    <div class="flex flex-col w-full gap-1 min-w-0">
      <div class="flex items-center justify-between h-6 gap-2">
        <h4 class="text-base font-medium truncate text-n-slate-12">
          {{ currentContactName }}
        </h4>
        <div class="flex items-center gap-2">
          <CardPriorityIcon :priority="conversation.priority || null" />
          <div
            v-tooltip.left="inboxName"
            class="flex items-center justify-center flex-shrink-0 rounded-full size-5"
            :style="inboxBadgeStyle"
          >
            <Icon
              :icon="inboxIcon"
              class="flex-shrink-0 text-white size-3"
            />
          </div>
          <span class="text-sm text-n-slate-10">
            {{ lastActivityAt }}
          </span>
        </div>
      </div>
      <CardMessagePreview
        v-show="showMessagePreviewWithoutMeta"
        :conversation="conversation"
      />
      <CardMessagePreviewWithMeta
        v-show="!showMessagePreviewWithoutMeta"
        ref="cardMessagePreviewWithMetaRef"
        :conversation="conversation"
        :account-labels="accountLabels"
      />
    </div>
  </div>
</template>
